import { motion } from "framer-motion";
import { MainNav } from "../components/MainNav";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";
import { useLanguage } from "../contexts/LanguageContext";
import { Ship, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { getVesselFleetSchema } from "../lib/structuredData";

const VesselsPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const seoContent = {
    title: language === 'sv' 
      ? 'Vår fartygflotta - Bunkerfartyg för leverans | Petrobell'
      : 'Our Fleet - Bunker Vessels for Delivery | Petrobell',
    description: language === 'sv'
      ? 'Petrobells bunkerfartyg: Långbjörn, Bunkerbjörn, Sjöbjörn 9, Storbjörn och Lillbjörn. Vi levererar drivmedel till hela Sveriges kust med vår moderna fartygflotta.'
      : 'Petrobell bunker vessels: Långbjörn, Bunkerbjörn, Sjöbjörn 9, Storbjörn and Lillbjörn. We deliver fuel to all of Sweden\'s coast with our modern fleet.',
    keywords: language === 'sv'
      ? 'bunkerfartyg, fartygflotta, bunkerleverans, marina drivmedel fartyg, fartygsleverans bränsle, Långbjörn, bunker Stockholm'
      : 'bunker vessels, vessel fleet, bunker delivery, marine fuel ship, ship fuel delivery, fuel delivery ship, bunker Stockholm'
  };

  const content = {
    ourVessels: language === 'sv' ? 'Våra Fartyg' : 'Our Vessels',
    subtitle: language === 'sv' 
      ? 'Vår flotta av bunkerfartyg är redo att leverera drivmedel till hela Sverige' 
      : 'Our fleet of bunker vessels is ready to deliver fuel throughout Sweden',
    tableHeaders: {
      name: language === 'sv' ? 'Namn' : 'Name',
      signal: 'Signal',
      capacity: language === 'sv' ? 'Kapacitet' : 'Capacity',
      length: language === 'sv' ? 'Största längd' : 'Maximum length',
      width: language === 'sv' ? 'Största bredd' : 'Maximum width',
      products: language === 'sv' ? 'Produkter' : 'Products'
    },
    imageGallery: language === 'sv' ? 'Bildgalleri' : 'Image Gallery',
    vesselProducts: {
      diesel: language === 'sv' ? 'Diesel' : 'Diesel',
      both: language === 'sv' ? 'Diesel och bensin' : 'Diesel and petrol'
    },
  };

  const vessels = [
    {
      name: "Långbjörn",
      signal: "SDFK",
      capacity: "160 m³",
      length: "29,52 m",
      width: "5,06 m",
      products: content.vesselProducts.diesel
    },
    {
      name: "Bunkerbjörn",
      signal: "SDAR",
      capacity: "95 m³",
      length: "20,33 m",
      width: "5,02 m",
      products: content.vesselProducts.both
    },
    {
      name: "Sjöbjörn 9",
      signal: "SHPW",
      capacity: "110 m³",
      length: "19,38 m",
      width: "5,15 m",
      products: content.vesselProducts.both
    },
    {
      name: "Storbjörn",
      signal: "SEKR",
      capacity: "70 m³",
      length: "18,2 m",
      width: "5,01 m",
      products: content.vesselProducts.both
    },
    {
      name: "Lillbjörn",
      signal: "SFC-7422",
      capacity: "25 m³",
      length: "11,28 m",
      width: "4,02 m",
      products: content.vesselProducts.diesel
    }
  ];

  const carouselImages = [
    {
      src: "/lovable-uploads/labj-sodra-sunset.jpg",
      alt: language === 'sv' ? "Bunkerfartyg i solnedgång - Petrobells flotta" : "Bunker vessel at sunset - Petrobell's fleet"
    },
    {
      src: "/lovable-uploads/1c2e2d7a-0245-44bb-a7c4-f38cf4ed4294.png",
      alt: language === 'sv' ? "M/T Långbjörn - Petrobells största bunkerfartyg vid leverans" : "M/T Långbjörn - Petrobell's largest bunker vessel during delivery"
    },
    {
      src: "/lovable-uploads/9da44368-4fd3-4ef7-846e-a30edfad8733.png",
      alt: language === 'sv' ? "Bunkerfartyg levererar drivmedel vid kaj i Stockholms skärgård" : "Bunker vessel delivering fuel at dock in Stockholm archipelago"
    },
    {
      src: "/lovable-uploads/d721b84e-0274-4e39-90c1-82ba44455caa.png",
      alt: language === 'sv' ? "Petrobells fartygflotta - Marina drivmedel levererade säkert" : "Petrobell's fleet - Marine fuels delivered safely"
    }
  ];

  return (
    <div className="min-h-screen w-full">
      <SEOHead
        title={seoContent.title}
        description={seoContent.description}
        path="/vessels"
        image="/lovable-uploads/d3bb8b77-b7ad-4d92-9ef4-b2f2686aa0da.png"
        language={language}
        keywords={seoContent.keywords}
        jsonLd={getVesselFleetSchema(language)}
      />
      <MainNav />
      
      {/* Hero Section */}
      <section id="vessels-hero" className="relative h-screen flex items-center justify-center bg-[url('/lovable-uploads/d3bb8b77-b7ad-4d92-9ef4-b2f2686aa0da.png')] bg-cover bg-center">
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => navigate('/')}
          className="absolute top-24 left-4 md:left-8 z-20 flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
        >
          <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
          <span>{language === 'sv' ? 'Tillbaka' : 'Back'}</span>
        </motion.button>
        
        <div className="container mx-auto px-4 z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-1 mb-6 text-sm font-medium bg-white/20 backdrop-blur-sm rounded-full text-white">
              Petrobell
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {content.ourVessels}
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              {content.subtitle}
            </p>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2" />
          </div>
        </motion.div>
      </section>

      {/* Vessels Section */}
      <section className="min-h-screen flex items-center justify-center py-20 bg-[url('/images/hav-bg.jpg')] bg-cover bg-center bg-fixed relative">
        <div className="absolute inset-0 bg-black/50" />
        <div className="container mx-auto px-4 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-12">{content.ourVessels}</h2>
            
            <div className="bg-white/10 backdrop-blur-md rounded-2xl overflow-hidden border border-white/20">
              {/* Header row - desktop only */}
              <div className="hidden md:grid grid-cols-6 gap-4 p-4 bg-white/10 border-b border-white/20">
                <span className="text-white font-semibold">{content.tableHeaders.name}</span>
                <span className="text-white font-semibold">{content.tableHeaders.signal}</span>
                <span className="text-white font-semibold">{content.tableHeaders.capacity}</span>
                <span className="text-white font-semibold">{content.tableHeaders.length}</span>
                <span className="text-white font-semibold">{content.tableHeaders.width}</span>
                <span className="text-white font-semibold">{content.tableHeaders.products}</span>
              </div>
              
              {/* Vessel rows */}
              {vessels.map((vessel, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`group hover:bg-white/10 transition-all duration-300 ${index !== vessels.length - 1 ? 'border-b border-white/10' : ''}`}
                >
                  {/* Desktop row */}
                  <div className="hidden md:grid grid-cols-6 gap-4 p-4 items-center">
                    <div className="flex items-center gap-2">
                      <Ship className="h-4 w-4 text-white/60" />
                      <span className="text-white font-semibold">{vessel.name}</span>
                    </div>
                    <span className="text-white/80">{vessel.signal}</span>
                    <span className="text-white/80">{vessel.capacity}</span>
                    <span className="text-white/80">{vessel.length}</span>
                    <span className="text-white/80">{vessel.width}</span>
                    <span className="text-white/80">{vessel.products}</span>
                  </div>
                  
                  {/* Mobile card */}
                  <div className="md:hidden p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Ship className="h-4 w-4 text-white/60" />
                      <span className="text-white font-semibold text-lg">{vessel.name}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-white/60">{content.tableHeaders.signal}</span>
                      <span className="text-white">{vessel.signal}</span>
                      <span className="text-white/60">{content.tableHeaders.capacity}</span>
                      <span className="text-white">{vessel.capacity}</span>
                      <span className="text-white/60">{content.tableHeaders.length}</span>
                      <span className="text-white">{vessel.length}</span>
                      <span className="text-white/60">{content.tableHeaders.width}</span>
                      <span className="text-white">{vessel.width}</span>
                      <span className="text-white/60">{content.tableHeaders.products}</span>
                      <span className="text-white">{vessel.products}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="h-screen relative overflow-hidden">
        <Carousel className="w-full h-full">
          <CarouselContent className="h-full">
            {carouselImages.map((image, index) => (
              <CarouselItem key={index} className="h-full">
                <div className="relative w-full h-screen">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30" />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-4 bg-white/20 backdrop-blur-sm border-white/30 text-white hover:bg-white/30 z-20" />
          <CarouselNext className="right-4 bg-white/20 backdrop-blur-sm border-white/30 text-white hover:bg-white/30 z-20" />
        </Carousel>
        
        {/* Title overlay */}
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="absolute top-24 left-8 text-4xl font-bold text-white z-10"
        >
          {content.imageGallery}
        </motion.h2>
      </section>
    </div>
  );
};

export default VesselsPage;
